import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  isMethodRowSavable,
  resolveScanEntityIdentifier,
  type MethodRow,
} from './testOptimizationUtils';
import type { ScanCallableEntity } from '@/components/smartai/api/getCallGraph';
import { saveTestRunSpec } from '@/components/smartai/api/saveTestRunSpec';
import {
  blastRadiusDepthOptions,
  buildEntitiesByKeyFromEntitiesByFile,
  computeNaturalMaxDepthForSeeds,
  formatBlastRadiusDepthLabel,
} from '@/components/smartai/CallGraph/call-graph-blast-radius';
import {
  saveSpecModalBodyClass,
  saveSpecModalCheckboxClass,
  saveSpecModalCheckboxHintClass,
  saveSpecModalCheckboxTextClass,
  saveSpecModalCheckboxTitleClass,
  saveSpecModalDepthHintClass,
  saveSpecModalDepthPanelClass,
  saveSpecModalDepthSelectClass,
  saveSpecModalErrorClass,
  saveSpecModalFieldClass,
  saveSpecModalFooterClass,
  saveSpecModalInputClass,
  saveSpecModalMethodsLabelClass,
  saveSpecModalMethodsSubClass,
  saveSpecModalOptionsClass,
  saveSpecModalPillClass,
  saveSpecModalPillsClass,
} from './test-optimization-layout';

export type SaveTestRunSpecModalProps = {
  visible: boolean;
  /**
   * "To" code-build id from Diff Check (numeric, e.g. ``320983``). Required —
   * the backend looks up the scans document by this id to resolve the rest of
   * the context (repoId, branchId, scan composite key, etc.).
   */
  buildId?: number | string;
  /**
   * "From" (older) code-build id from Diff Check. Optional. When provided it
   * is persisted on the spec so the saved record captures both sides of the
   * comparison.
   */
  fromBuildId?: number | string;
  /** Optional legacy composite scan id. Kept for callers that still have it. */
  scanId?: string;
  selectedMethods: MethodRow[];
  /** Full scan callable map for dependency depth (same source as call graph). */
  entitiesByFile?: Record<string, ScanCallableEntity[]>;
  onClose: () => void;
  onSaved?: (specName: string) => void;
};

function formatMethodLabel(row: MethodRow): string {
  const name = row.name?.trim() || 'unknown';
  if (name === 'constructor' || row.type === 'Constructor')
    return 'constructor';
  return `${name}()`;
}

const SaveTestRunSpecModal = ({
  visible,
  buildId,
  fromBuildId,
  scanId,
  selectedMethods,
  entitiesByFile,
  onClose,
  onSaved,
}: SaveTestRunSpecModalProps) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [includeSelectedMethodTests, setIncludeSelectedMethodTests] =
    useState(true);
  const [includeLatestBuildTests, setIncludeLatestBuildTests] = useState(false);
  const [includeFailedBuildTests, setIncludeFailedBuildTests] = useState(false);
  const [fanDepth, setFanDepth] = useState(1);

  const entitiesByKey = useMemo(
    () => buildEntitiesByKeyFromEntitiesByFile(entitiesByFile ?? {}),
    [entitiesByFile],
  );

  const selectedSeedKeys = useMemo(
    () => selectedMethods.map(row => resolveScanEntityIdentifier(row)),
    [selectedMethods],
  );

  const naturalMaxDepth = useMemo(
    () => computeNaturalMaxDepthForSeeds(selectedSeedKeys, entitiesByKey),
    [selectedSeedKeys, entitiesByKey],
  );

  const depthOptions = useMemo(
    () => blastRadiusDepthOptions(naturalMaxDepth.maxDepth),
    [naturalMaxDepth.maxDepth],
  );

  useEffect(() => {
    if (visible) {
      setName('');
      setError(null);
      setSaving(false);
      setIncludeSelectedMethodTests(true);
      setIncludeLatestBuildTests(false);
      setIncludeFailedBuildTests(false);
      setFanDepth(1);
    }
  }, [visible]);

  useEffect(() => {
    if (fanDepth > naturalMaxDepth.maxDepth) {
      setFanDepth(naturalMaxDepth.maxDepth);
    }
  }, [fanDepth, naturalMaxDepth.maxDepth]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Please enter a name for this test run spec.');
      return;
    }
    if (buildId == null && !scanId) {
      setError('Scan context is missing. Re-open Test Optimization from Build Diff.');
      return;
    }
    const savableMethods = selectedMethods.filter(isMethodRowSavable);
    if (savableMethods.length === 0) {
      setError(
        'Selected methods are not linked to scan entities and cannot be saved. Select rows that matched the build-to scan.',
      );
      return;
    }
    if (
      !includeSelectedMethodTests &&
      !includeLatestBuildTests &&
      !includeFailedBuildTests
    ) {
      setError('Select at least one test case source.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await saveTestRunSpec({
        ...(scanId ? { scanId } : { buildId }),
        fromBuildId,
        name: trimmed,
        entityKeys: savableMethods.map(m => resolveScanEntityIdentifier(m)),
        methodNames: savableMethods.map(m => formatMethodLabel(m)),
        includeSelectedMethodTests,
        includeLatestBuildTests,
        includeFailedBuildTests,
        fanDepth: includeSelectedMethodTests ? fanDepth : undefined,
      });
      onSaved?.(trimmed);
      onClose();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (
              err as {
                response?: { data?: { message?: string; detail?: string } };
              }
            ).response?.data?.message ||
            (err as { response?: { data?: { detail?: string } } }).response
              ?.data?.detail
          : err instanceof Error
            ? err.message
            : 'Failed to save test run spec';
      setError(typeof msg === 'string' ? msg : 'Failed to save test run spec');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={visible} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Save Test Run Spec</DialogTitle>
        </DialogHeader>

        <div className={saveSpecModalBodyClass}>
          <div className={saveSpecModalFieldClass}>
            <label htmlFor="test-run-spec-name">Name this Test Run Spec</label>
            <input
              id="test-run-spec-name"
              type="text"
              className={saveSpecModalInputClass}
              placeholder="e.g. Sprint 22 May — Critical Coverage Run"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={saving}
              onKeyDown={e => {
                if (e.key === 'Enter') void handleSave();
              }}
            />
          </div>

          <div>
            <p className={saveSpecModalMethodsLabelClass}>Selected Methods</p>
            <p className={saveSpecModalMethodsSubClass}>
              Priority, Batch and Tests will be computed on save
            </p>
            <div className={saveSpecModalPillsClass}>
              {selectedMethods.map(row => (
                <span key={row.entityKey} className={saveSpecModalPillClass}>
                  {formatMethodLabel(row)}
                </span>
              ))}
            </div>
          </div>

          <div className={saveSpecModalOptionsClass}>
            <p className={saveSpecModalMethodsLabelClass}>Test Case Sources</p>
            <p className={saveSpecModalMethodsSubClass}>
              Choose which test cases to include in this spec
            </p>

            <div>
              <label className={saveSpecModalCheckboxClass}>
                <input
                  type="checkbox"
                  checked={includeSelectedMethodTests}
                  onChange={e =>
                    setIncludeSelectedMethodTests(e.target.checked)
                  }
                  disabled={saving}
                />
                <span className={saveSpecModalCheckboxTextClass}>
                  <span className={saveSpecModalCheckboxTitleClass}>
                    Include test cases for selected methods (with dependencies)
                  </span>
                  <span className={saveSpecModalCheckboxHintClass}>
                    Pull tests that cover the methods chosen above along with
                    their upstream / downstream dependencies
                  </span>
                </span>
              </label>

              {includeSelectedMethodTests && (
                <div className={saveSpecModalDepthPanelClass}>
                  <p className={saveSpecModalDepthHintClass}>
                    Natural max:{' '}
                    <span className="font-semibold text-slate-600">
                      {naturalMaxDepth.maxFanInDepth}
                    </span>{' '}
                    fan-in ·{' '}
                    <span className="font-semibold text-slate-600">
                      {naturalMaxDepth.maxFanOutDepth}
                    </span>{' '}
                    fan-out
                  </p>
                  <label>
                    <span className="sr-only">Dependency depth</span>
                    <select
                      className={saveSpecModalDepthSelectClass}
                      value={fanDepth}
                      onChange={event =>
                        setFanDepth(Number(event.target.value))
                      }
                      disabled={saving}
                      aria-label="Dependency depth">
                      {depthOptions.map(level => (
                        <option key={level} value={level}>
                          {formatBlastRadiusDepthLabel(
                            level,
                            naturalMaxDepth.maxDepth,
                          )}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
            </div>

            <label className={saveSpecModalCheckboxClass}>
              <input
                type="checkbox"
                checked={includeLatestBuildTests}
                onChange={e => setIncludeLatestBuildTests(e.target.checked)}
                disabled={saving}
              />
              <span className={saveSpecModalCheckboxTextClass}>
                <span className={saveSpecModalCheckboxTitleClass}>
                  Include test cases from changed scripts in the latest build
                </span>
                <span className={saveSpecModalCheckboxHintClass}>
                  Add tests linked to any script that was modified in the most
                  recent build
                </span>
              </span>
            </label>

            <label className={saveSpecModalCheckboxClass}>
              <input
                type="checkbox"
                checked={includeFailedBuildTests}
                onChange={e => setIncludeFailedBuildTests(e.target.checked)}
                disabled={saving}
              />
              <span className={saveSpecModalCheckboxTextClass}>
                <span className={saveSpecModalCheckboxTitleClass}>
                  Include failed test cases of this script build
                </span>
                <span className={saveSpecModalCheckboxHintClass}>
                  {`Only the tests that failed in the selected script's build run`}
                </span>
              </span>
            </label>
          </div>

          {error && <p className={saveSpecModalErrorClass}>{error}</p>}
        </div>

        <DialogFooter className={saveSpecModalFooterClass}>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save Spec'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveTestRunSpecModal;
